import type { TitleSize } from '../components/typography/title'
import type { SubtitleSize } from '../components/typography/subtitle'
import type { BodySize } from '../components/typography/body'
import type { SmallSize } from '../components/typography/small'
import type { LargeSize } from '../components/typography/large'

/**
 * Icon size classes for kiosk surfaces.
 */
export type IconSize = 'size-10' | 'size-12' | 'size-14' | 'size-16' | 'size-20' | 'size-24'

/**
 * Button size values
 */
export type ButtonSize = 'default' | 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'

/**
 * Gap size classes for spacing
 */
export type GapSize = 'gap-2' | 'gap-3' | 'gap-4' | 'gap-5' | 'gap-6' | 'gap-8' | 'gap-10'

/**
 * Sizing roles returned by {@link useSizing}.
 *
 * These name a *role* in the kiosk hierarchy ("this is the hero icon", "this is
 * the section gap"), not a physical size. The physical size of each role is
 * resolved fluidly at paint time by the `.frontdesk-kiosk-shell` token scale in
 * `packages/ui/index.css`.
 */
export interface SizingValues {
  // Typography roles
  title: TitleSize
  subtitle: SubtitleSize
  body: BodySize
  small: SmallSize
  large: LargeSize

  // Button roles
  buttonPrimary: ButtonSize
  buttonSecondary: ButtonSize

  // Icon roles (Tailwind classes)
  heroIcon: IconSize
  cardIcon: IconSize
  inlineIcon: IconSize

  // Spacing roles (Tailwind classes)
  containerGap: GapSize
  cardGap: GapSize
  sectionGap: GapSize
}

/**
 * The kiosk's sizing roles.
 *
 * This used to be one of six viewport presets selected by hard width
 * thresholds (1024 / 1366 / 1440 / 1920 / 2560). That made the kiosk snap
 * between sizes as a window was resized across a threshold, and — because the
 * thresholds looked at width only — handed a short, wide screen the largest
 * preset precisely where there was least vertical room, which is how the hub
 * came to overflow at 1920x700.
 *
 * The roles are therefore constant. Every value below resolves through a
 * Tailwind token (`text-*`, `size-*`, `gap-*`) that the kiosk shell redefines
 * as a continuous function of the viewport's *smaller* axis, so the same role
 * is physically smaller on a cramped screen and larger on a 4K one without any
 * threshold in between. The values are the ones the old `largeTablet` preset
 * used, and the token scale is anchored so that a 1440x900 kiosk — the design
 * centre — renders exactly as it did before.
 */
const KIOSK_SIZING: SizingValues = {
  title: 'xl',
  subtitle: 'xl',
  body: 'lg',
  small: 'default',
  large: 'xl',
  buttonPrimary: '2xl',
  buttonSecondary: 'xl',
  heroIcon: 'size-16',
  cardIcon: 'size-14',
  inlineIcon: 'size-16',
  containerGap: 'gap-8',
  cardGap: 'gap-6',
  sectionGap: 'gap-10',
}

/**
 * Hook returning the kiosk's sizing roles.
 *
 * The returned object is referentially stable and viewport-independent: fluid
 * scaling is applied by the `.frontdesk-kiosk-shell` token scale rather than by
 * re-rendering React on resize. Readability mode scales the same tokens, so it
 * needs no wiring here either.
 *
 * @since 1.0.0
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const sizing = useSizing()
 *
 *   return (
 *     <div className={sizing.containerGap}>
 *       <Title size={sizing.title}>Welcome</Title>
 *       <Subtitle size={sizing.subtitle}>Please select an option</Subtitle>
 *       <Button size={sizing.buttonPrimary}>Continue</Button>
 *       <CheckIcon className={sizing.heroIcon} />
 *     </div>
 *   )
 * }
 * ```
 */
export function useSizing(): SizingValues {
  return KIOSK_SIZING
}
